import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { CheckCircle, ClipboardList, Calendar } from 'lucide-react';
import { useMilestones, useMarkMilestoneComplete } from '@/hooks/useMilestones';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import type { Milestone } from '@/lib/types/milestone';

interface MilestoneCompletionReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  userRole: 'client' | 'vendor';
  uploadedCategory: string;
  onComplete?: () => void;
}

export function MilestoneCompletionReminderDialog({ 
  open, 
  onOpenChange, 
  jobId,
  userRole,
  uploadedCategory,
  onComplete 
}: MilestoneCompletionReminderDialogProps) {
  const { milestones, loading: milestonesLoading } = useMilestones(jobId);
  const { markComplete, loading: markingComplete } = useMarkMilestoneComplete();
  const { toast } = useToast();
  const [incompleteMilestones, setIncompleteMilestones] = useState<Milestone[]>([]);
  const [selectedMilestoneId, setSelectedMilestoneId] = useState<string | null>(null);

  useEffect(() => {
    if (!milestonesLoading && milestones) {
      // Find incomplete user-generated milestones owned by the current user's role
      const userMilestones = milestones.filter(
        m => !m.is_system_milestone && 
             !m.is_completed && 
             m.action_owner === userRole
      );
      setIncompleteMilestones(userMilestones);
      // Auto-select if only one milestone
      if (userMilestones.length === 1) {
        setSelectedMilestoneId(userMilestones[0].id);
      }
    }
  }, [milestones, milestonesLoading, userRole]);

  const handleMarkComplete = async () => {
    if (!selectedMilestoneId) return;

    try {
      await markComplete(selectedMilestoneId);
      const milestone = incompleteMilestones.find(m => m.id === selectedMilestoneId);
      toast({
        title: "Milestone Completed",
        description: `"${milestone?.milestone_name}" has been marked as complete.`,
      });
      onOpenChange(false);
      onComplete?.();
    } catch (error) {
      console.error('Error marking milestone complete:', error);
      toast({
        title: "Error",
        description: "Failed to mark milestone as complete. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDismiss = () => {
    setSelectedMilestoneId(null);
    onOpenChange(false);
  };

  // Don't show if no incomplete milestones for this user
  if (incompleteMilestones.length === 0) return null;

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      artwork_files: 'artwork files',
      proofs: 'proofs',
      deliverables: 'deliverables',
      proof_of_delivery: 'proof of delivery',
      shipment: 'shipping information'
    };
    return labels[category] || 'files';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            Reminder: Check Your Milestones
          </DialogTitle>
          <DialogDescription>
            You've uploaded {getCategoryLabel(uploadedCategory)}. If this upload completes a milestone, you can mark it done now.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm font-medium mb-3">Your incomplete milestones:</p>
          <RadioGroup 
            value={selectedMilestoneId || ''} 
            onValueChange={setSelectedMilestoneId}
            className="space-y-2"
          >
            {incompleteMilestones.map((milestone) => (
              <div 
                key={milestone.id} 
                className="flex items-start space-x-3 border rounded-lg p-3 hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => setSelectedMilestoneId(milestone.id)}
              >
                <RadioGroupItem value={milestone.id} id={milestone.id} className="mt-1" />
                <Label htmlFor={milestone.id} className="flex-1 cursor-pointer">
                  <div className="font-medium">{milestone.milestone_name}</div>
                  {milestone.due_date && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                      <Calendar className="h-3 w-3" />
                      Due: {format(new Date(milestone.due_date), 'MMM d, yyyy')}
                    </div>
                  )}
                  {milestone.deliverable && (
                    <div className="text-sm text-muted-foreground mt-1">
                      {milestone.deliverable}
                    </div>
                  )}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleDismiss}
            disabled={markingComplete}
          >
            I'll Do This Later
          </Button>
          <Button 
            onClick={handleMarkComplete}
            disabled={!selectedMilestoneId || markingComplete}
            className="bg-green-600 hover:bg-green-700"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {markingComplete ? 'Marking Complete...' : 'Mark Complete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}